/* Labelled text input wrapping the .input + .label brand classes from index.css.
   Generates a stable id via useId when label is present and no id prop is supplied,
   so the label/input pair is always linked. Error state sets aria-invalid and
   aria-describedby; the error message uses text-caption + red palette with dark
   variant. Forwards the ref to the underlying <input> element. */
import { forwardRef, useId, type InputHTMLAttributes } from 'react';
import { clsx } from 'clsx';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, id, className, ...props }, ref) => {
    // Stable generated id — only used when a label is present and no id was
    // supplied, so label and input are always associated via htmlFor/id.
    const generatedId = useId();
    const inputId = id ?? (label ? generatedId : undefined);
    const errorId = error ? `${generatedId}-error` : undefined;

    return (
      <div>
        {label && (
          <label className="label" htmlFor={inputId}>
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={clsx('input', className)}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          {...props}
        />
        {error && (
          <p
            id={errorId}
            className="text-caption text-red-600 dark:text-red-400 mt-1"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
